import { ReactNode, useRef, useState } from "react";
import { faEnvelope, faPhone, faSms } from "@fortawesome/free-solid-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { H5 } from "@verse_core/shared_components/base/TextComponents/H5";
import { P1 } from "@verse_core/shared_components/base/TextComponents/P1";
import { copyToClipboard } from "./util/domUtil";

/**
 * A reusable contact card component that displays a title, email, and phone number for support.
 *
 * This component renders a card with the following features:
 *  - A title indicating the type of support (e.g., "For specific questions about your order")
 *  - An email address with a "Copy" button that allows users to copy the email to the clipboard
 *  - A phone number for direct contact
 *
 * @param title - The title indicating the type of support
 * @param email - The support email address that can be copied to the clipboard
 * @param phoneNumber - The support phone number
 * @param textNumber - The support text number
 * @param children - Extra content rendered below the contact rows
 */
const ContactCard = ({
  title,
  email,
  phoneNumber,
  textNumber,
  className,
  children,
}: {
  title: string;
  email: string;
  phoneNumber?: string;
  textNumber?: string;
  className: string;
  children?: ReactNode;
}) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null); // Store the timeout reference

  const handleCopy = () => {
    copyToClipboard(email);
    setCopied(true);

    // Clear the previous timeout if it exists
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set a new timeout to reset the copied state after 2 seconds
    timeoutRef.current = setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  return (
    <div
      className={`tw-flex-grow tw-rounded-lg tw-border-[1px] tw-border-solid tw-border-gray-light-1 tw-p-[24px] ${className}`}
    >
      <H5 className="tw-mb-[24px] tw-mt-[0px] tw-font-bold tw-text-blue-dark-1">
        {title}
      </H5>
      <div className="tw-flex tw-flex-col tw-gap-[16px]">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-y-[10px]">
          <div className="tw-mr-[16px] tw-flex tw-h-[32px] tw-w-[32px] tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-light-1">
            <FontAwesomeIcon
              icon={faEnvelope}
              className="tw-text-[16px] tw-text-blue-dark-1"
            />
          </div>
          <P1 className="tw-mb-0 tw-mr-[16px] tw-break-all"> {email} </P1>
          <div
            className={`tw-flex tw-h-[24px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-[3px] tw-text-[14px] ${
              copied
                ? "tw-w-[60px] tw-bg-green-light-1 tw-text-green-1"
                : "tw-w-[43px] tw-bg-gray-light-2 tw-transition-colors tw-duration-500"
            } `}
            onClick={handleCopy}
          >
            <span className="tw-m-0">{copied ? "Copied!" : "Copy"}</span>
          </div>
        </div>
        {textNumber && (
          <div className="tw-flex tw-items-center">
            <div className="tw-mr-[16px] tw-flex tw-h-[32px] tw-w-[32px] tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-light-1">
              <FontAwesomeIcon
                icon={faSms}
                className="tw-text-[16px] tw-text-blue-dark-1"
              />
            </div>
            <P1 className="tw-mb-0 tw-min-w-0">{textNumber}</P1>
          </div>
        )}
        {phoneNumber && (
          <div className="tw-flex tw-items-center">
            <div className="tw-mr-[16px] tw-flex tw-h-[32px] tw-w-[32px] tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-light-1">
              <FontAwesomeIcon
                icon={faPhone}
                className="tw-text-[16px] tw-text-blue-dark-1"
              />
            </div>
            <P1 className="tw-m-0"> {phoneNumber} </P1>
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

export default ContactCard;
